import { css, html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";

interface ISearchSuggestion {
  title: string;
  url: string;
}

@customElement("ehm-suggest")
export class EhmSuggest extends LitElement {
  static styles = css`
    :host {
    }
    .listbox {
      color: var(--text-primary);
      position: absolute;
      display: block;
      font-family: system-ui, sans-serif;
      font-size: 14px;
      z-index: 999;
      top: calc(100% - 0.5rem);
      left: 0;
      right: 0;
      padding: 1rem 0;
      background: var(--theme-base);
      border: 1px solid var(--theme-border);
      border-radius: var(--radius-sm);
      box-shadow: 0 12px 16px -4px rgba(10, 13, 18, 0.08),
      0 4px 6px -2px rgba(10, 13, 18, 0.03),
      0 2px 2px -1px rgba(10, 13, 18, 0.04);
      max-height: 240px;
      overflow-y: auto;
    }
    .listbox.static {
      position: static;
      max-height: none;
      box-shadow: none;
      background: transparent;
      overflow-y: visible;
      border: none;
      margin-top: -1.5rem;
    }
    .listbox.absolute {
      padding-bottom: 0;
      padding-top: 0;
      & .item {
        padding-left: 14px;
        padding-right: 14px;
        &:first-child {
          margin-top: 0.5rem;
        }
        &:last-child {
          margin-top: 0.5rem;
        }
      }
    }
    .item {
      display: flex;
      align-items: flex-start;
      padding: 5px 0;
      cursor: pointer;
      user-select: none;
      font: var(--font-text-md);
      gap: 0.625rem;
      --icon-size: 1.25rem;
      --icon-offset: 3px;
    }
    .item.active,
    .item:hover {
      background: var(--theme-bg_hover-darker);
    }
    mark {
      font-weight: var(--text-bold);
      background: none;
      color: inherit;
    }
  `;

  @property({ type: String }) for = "";
  @property({ type: String }) endpoint: string | undefined;
  @property({ type: Number }) debounceMs = 200;
  @property({ type: Number }) minChars = 3;
  @property({ type: String }) position: "static" | "absolute" = "absolute";
  @property({ type: String, attribute: "action-type" }) actionType:
    | "search"
    | "navigate" = "search";
  @property({ type: String, attribute: "response-key" }) responseKey: string | undefined;

  @state() private query = "";
  @state() private suggestions: ISearchSuggestion[] = [];
  @state() private activeIndex = -1;
  @state() private open = false;

  private inputEl?: HTMLInputElement | null;
  private debounceHandle?: number;
  private fetchAbort?: AbortController;

  connectedCallback() {
    super.connectedCallback();
    this.attachToInput();
    document.addEventListener("pointerdown", this.onDocumentPointerDown, true);
    document.addEventListener("search-executed", this.onSearchExecuted);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.detachFromInput();
    if (this.fetchAbort) this.fetchAbort.abort();
    document.removeEventListener("pointerdown", this.onDocumentPointerDown, true);
    document.removeEventListener("search-executed", this.onSearchExecuted);
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has("open")) {
      if (!this.open) {
        this.activeIndex = -1;
      }
      if (this.inputEl) {
        this.inputEl.setAttribute("aria-expanded", String(this.open));
      }
    }

    if (changed.has("activeIndex")) {
      this.updateActivedescendant();
      this.scrollActiveIntoView();
    }

    if (changed.has("for")) {
      this.detachFromInput();
      this.attachToInput();
    }
  }

  render() {
    if (!this.open || this.suggestions.length === 0) return null;
    return html`
      <ul
        class=${`listbox ${this.position}`}
        @mousedown=${this.onMouseDownList}
        role="listbox"
        id="suggestions"
      >
        ${this.suggestions.map(
          (suggestion, i) => html`
            <li
              class="item ${i === this.activeIndex ? "active" : ""}"
              aria-selected=${i === this.activeIndex}
              @click=${() => this.commitSelection(i)}
              role="option"
              id="suggestion_${i}"
            >
              <ehm-icon name="arrow"></ehm-icon>
              <span
              >${unsafeHTML(this.highlightQuery(suggestion.title, this.query))}</span
              >
            </li>
          `
        )}
      </ul>
    `;
  }

  private onSearchExecuted = () => {
    if (this.debounceHandle) {
      clearTimeout(this.debounceHandle);
    }
    if (this.fetchAbort) {
      this.fetchAbort.abort();
    }
    this.open = false;
  };

  private attachToInput() {
    if (!this.for) return;
    this.inputEl = document.getElementById(this.for) as HTMLInputElement | null;
    if (!this.inputEl) return;

    this.inputEl.setAttribute("aria-autocomplete", "list");
    this.inputEl.setAttribute("aria-haspopup", "listbox");
    this.inputEl.setAttribute("aria-expanded", "false");
    this.inputEl.setAttribute("aria-controls", "suggestions");
    this.inputEl.setAttribute("aria-activedescendant", "");

    this.inputEl.addEventListener("input", this.onInput);
    this.inputEl.addEventListener("keydown", this.onKeyDown);
    this.inputEl.addEventListener("focus", this.onFocus);
  }

  private detachFromInput() {
    if (!this.inputEl) return;
    this.inputEl.removeEventListener("input", this.onInput);
    this.inputEl.removeEventListener("keydown", this.onKeyDown);
    this.inputEl.removeEventListener("focus", this.onFocus);
    this.inputEl = null;
  }

  private highlightQuery(phrase: string, query: string) {
    if (!query || query.length < 3) return phrase;
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedQuery})`, "gi");
    return phrase.replace(regex, "<mark>$1</mark>");
  }

  private onInput = (e: Event) => {
    this.query = (e.target as HTMLInputElement).value;
    this.scheduleFetch();
  };

  private onFocus = () => {
    if ((this.inputEl?.value.length ?? 0) < this.minChars) {
      this.suggestions = [];
    }
    if (this.suggestions.length > 0) this.open = true;
  };

  private onKeyDown = (e: KeyboardEvent) => {
    if (!this.open && e.key !== "ArrowDown") return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        this.moveActive(1);
        break;
      case "ArrowUp":
        e.preventDefault();
        this.moveActive(-1);
        break;
      case "Enter":
        if (this.activeIndex >= 0) {
          e.preventDefault();
          this.commitSelection(this.activeIndex);
          this.open = false;
        }
        break;
      case "Escape":
        this.open = false;
        break;
      case "Tab":
        this.open = false;
        break;
    }
  };

  private moveActive(delta: number) {
    if (this.suggestions.length === 0) return;
    const len = this.suggestions.length;
    const next = this.activeIndex + delta;

    if (next < -1) {
      // Pressing up from input jumps to last item
      this.activeIndex = len - 1;
    } else if (next < 0) {
      // Pressing up from first item returns focus to input
      this.activeIndex = -1;
    } else if (next >= len) {
      // Pressing down from last item returns focus to input
      this.activeIndex = -1;
    } else {
      this.activeIndex = next;
    }
  }

  private onMouseDownList = (e: MouseEvent) => {
    e.preventDefault();
  };

  private onDocumentPointerDown = (e: PointerEvent) => {
    if (!this.open) return;
    const path = (e.composedPath && e.composedPath()) || [];
    const first = (path[0] as Node) || (e.target as Node | null);
    const inShadow = this.renderRoot?.contains((first ?? null) as any) ?? false;
    const inHost = (first ? this.contains(first) : false) || false;
    const inInput = this.inputEl ? this.inputEl.contains(first as Node) : false;
    const inside = inShadow || inHost || inInput;
    if (!inside) {
      this.open = false;
    }
  };

  private commitSelection(index: number) {
    const item = this.suggestions[index];
    if (!item || !this.inputEl) return;
    if (item.url && this.actionType === "navigate") {
      window.location.href = item.url;
      return;
    }
    this.inputEl.value = item.title;
    this.open = false;
    this.dispatchEvent(
      new CustomEvent("ehm-select", {
        detail: item,
        bubbles: true,
        composed: true
      })
    );
    this.inputEl.focus();
    this.inputEl.form?.dispatchEvent(
      new Event("submit", { cancelable: true, bubbles: true })
    );
  }

  private scheduleFetch() {
    if (this.debounceHandle) clearTimeout(this.debounceHandle);
    this.debounceHandle = window.setTimeout(() => this.fetchData(), this.debounceMs);
  }

  private async fetchData() {
    const q = this.query.trim();
    if (!this.endpoint || q.length < this.minChars) {
      this.suggestions = [];
      this.open = false;
      return;
    }

    if (this.fetchAbort) this.fetchAbort.abort();
    this.fetchAbort = new AbortController();

    try {
      const res = await fetch(`${this.endpoint}&term=${encodeURIComponent(q)}`, {
        signal: this.fetchAbort.signal,
        headers: { Accept: "application/json" }
      });
      const result = await res.json();
      const predictions = this.extractSuggestions(result);
      this.suggestions = predictions;
      this.open = predictions.length > 0;
    } catch {
      this.suggestions = [];
      this.open = false;
    }
  }

  private extractSuggestions(result: unknown): ISearchSuggestion[] {
    if (this.responseKey && result && typeof result === "object" && !Array.isArray(result)) {
      const nested = (result as Record<string, unknown>)[this.responseKey];
      return Array.isArray(nested) ? nested : [];
    }
    return Array.isArray(result) ? result : [];
  }

  private updateActivedescendant() {
    if (!this.inputEl) return;
    if (this.activeIndex >= 0) {
      this.inputEl.setAttribute(
        "aria-activedescendant",
        `suggestion_${this.activeIndex}`
      );
    } else {
      this.inputEl.setAttribute("aria-activedescendant", "");
    }
  }

  private scrollActiveIntoView() {
    if (this.activeIndex < 0) return;
    const list = this.renderRoot.querySelector(".listbox");
    if (!list) return;
    const active = list.querySelector<HTMLElement>(`#suggestion_${this.activeIndex}`);
    if (!active) return;

    active.scrollIntoView({ block: "nearest" });
  }
}